/**
 * Gerência — os números que fazem o dono abrir o sistema por vontade própria.
 *
 * Spec §2.6: "rendimento não é relatório que alguém escreve, é consulta".
 * Esta tela é fina porque o trabalho está no modelo de dados.
 */
import { useEffect, useState } from "react";
import { api } from "../api.ts";

interface Painel {
  estoque_m3: string; rendimento_90d: string; bitolas_paradas: string;
  margem_mes: string; sem_custo_m3: string;
}

interface Rendimento {
  especie: string; mes: string; desdobros: string;
  tora_m3: string; serrada_m3: string; perda_m3: string; rendimento: string | null;
}

interface Margem {
  especie: string; mes: string;
  vendido_m3: string | null; receita: string | null;
  comprado_m3: string | null; custo: string | null;
  margem: string; sem_custo_m3: string;
}

interface Giro {
  codigo: string; especie: string;
  esp_mm: number | null; larg_mm: number | null; comp_m: string | null;
  em_estoque_m3: string; vendido_m3: string; vendas: string;
  dias_parado: number | null; giro: string | null;
}

const n3 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const n2 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mes = (v: string) =>
  new Date(v).toLocaleDateString("pt-BR", { month: "2-digit", year: "numeric" });
const bitola = (g: Giro) =>
  g.esp_mm === null ? "tora" : `${g.esp_mm} × ${g.larg_mm} × ${g.comp_m}`;

/** Verde acima de 55%, âmbar entre 45 e 55, vermelho abaixo. */
function corRendimento(r: number): string {
  if (r >= 0.55) return "var(--ok-num)";
  if (r >= 0.45) return "var(--aten-num)";
  return "var(--erro-texto)";
}

/**
 * A cor sozinha não informa.
 *
 * "48,4%" em verde só quer dizer alguma coisa para quem já sabe qual é a faixa
 * boa de rendimento. Na tela de decisão o número vem com o veredito escrito —
 * e serve também para quem não distingue as três cores.
 */
function veredito(r: number): string {
  if (r >= 0.55) return "bom";
  if (r >= 0.45) return "aceitável";
  return "abaixo do esperado";
}

export function Gerencia() {
  const [p, setP] = useState<Painel | null>(null);
  const [rend, setRend] = useState<Rendimento[] | null>(null);
  const [marg, setMarg] = useState<Margem[] | null>(null);
  const [giro, setGiro] = useState<Giro[] | null>(null);

  useEffect(() => {
    void api.get<Painel>("/gerencia/painel").then(setP).catch(() => {});
    void api.get<{ linhas: Rendimento[] }>("/gerencia/rendimento")
      .then((r) => setRend(r.linhas)).catch(() => setRend([]));
    void api.get<{ linhas: Margem[] }>("/gerencia/margem")
      .then((r) => setMarg(r.linhas)).catch(() => setMarg([]));
    void api.get<{ linhas: Giro[] }>("/gerencia/giro")
      .then((r) => setGiro(r.linhas)).catch(() => setGiro([]));
  }, []);

  const semCusto = p !== null && Number(p.sem_custo_m3) > 0;

  return (
    <>
      <div className="cab-doc">
        <h1>Gerência</h1>
        <span className="cod">rendimento, margem e giro — direto da razão</span>
      </div>

      <div className="metricas">
        <div>
          <span className="rotulo">Estoque</span>
          <span className="vl">{p ? n3(p.estoque_m3) : "—"} m³</span>
          <span className="ct">no pátio</span>
        </div>
        <div>
          <span className="rotulo">Rendimento 90d</span>
          <span className="vl" style={{ color: p ? corRendimento(Number(p.rendimento_90d)) : undefined }}>
            {p ? (Number(p.rendimento_90d) * 100).toFixed(1) : "—"}%
          </span>
          <span className="ct">
            serrada ÷ tora
            {p && ` · ${veredito(Number(p.rendimento_90d))}`}
          </span>
        </div>
        <div>
          <span className="rotulo">Margem do mês</span>
          <span className="vl">R$ {p ? n2(p.margem_mes) : "—"}</span>
          <span className="ct">{semCusto ? "incompleta" : "receita − custo"}</span>
        </div>
        <div>
          <span className="rotulo">Bitolas paradas</span>
          <span className="vl" style={{
            color: p && Number(p.bitolas_paradas) > 0 ? "var(--aten-num)" : undefined,
          }}>
            {p?.bitolas_paradas ?? "—"}
          </span>
          <span className="ct">há mais de 60 dias</span>
        </div>
      </div>

      <div className="rolagem">
        {/* Aviso honesto: margem sobre custo parcial parece certa e não é. */}
        {semCusto && (
          <div className="painel" style={{
            borderColor: "var(--aten-borda)", background: "var(--aten-fundo)", padding: 12,
          }}>
            <span className="rotulo" style={{ color: "var(--aten-texto)" }}>Margem incompleta</span>
            <div style={{ marginTop: 4, color: "var(--aten-texto)", fontSize: 14 }}>
              {n3(p!.sem_custo_m3)} m³ entraram ou saíram <b>sem valor registrado</b> e ficaram de
              fora da conta. Informe o valor nas entradas para a margem fechar.
            </div>
          </div>
        )}

        <div className="painel">
          <div className="tt"><span className="rotulo">Rendimento por espécie</span></div>
          {rend === null ? <Esq /> : rend.length === 0 ? (
            <div className="vazio"><p>Nenhum desdobro registrado ainda.</p></div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th>Espécie</th><th>Mês</th>
                  <th style={{ textAlign: "right" }}>Desdobros</th>
                  <th style={{ textAlign: "right" }}>Tora m³</th>
                  <th style={{ textAlign: "right" }}>Serrada m³</th>
                  <th style={{ textAlign: "right" }}>Perda m³</th>
                  <th style={{ textAlign: "right" }}>Rendimento</th>
                </tr>
              </thead>
              <tbody>
                {rend.map((l, i) => (
                  <tr key={i}>
                    <td><b>{l.especie}</b></td>
                    <td className="mono">{mes(l.mes)}</td>
                    <td className="r">{l.desdobros}</td>
                    <td className="r">{n3(l.tora_m3)}</td>
                    <td className="r">{n3(l.serrada_m3)}</td>
                    <td className="r" style={{ color: "var(--texto-terciario)" }}>{n3(l.perda_m3)}</td>
                    <td className="r" style={{
                      fontWeight: 700,
                      color: l.rendimento ? corRendimento(Number(l.rendimento)) : undefined,
                    }}>
                      {l.rendimento ? `${(Number(l.rendimento) * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="painel">
          <div className="tt"><span className="rotulo">Margem por espécie</span></div>
          {marg === null ? <Esq /> : marg.length === 0 ? (
            <div className="vazio"><p>Nenhuma compra ou venda registrada.</p></div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th>Espécie</th><th>Mês</th>
                  <th style={{ textAlign: "right" }}>Vendido m³</th>
                  <th style={{ textAlign: "right" }}>Receita</th>
                  <th style={{ textAlign: "right" }}>Custo</th>
                  <th style={{ textAlign: "right" }}>Margem</th>
                  <th style={{ textAlign: "right" }}>Sem custo</th>
                </tr>
              </thead>
              <tbody>
                {marg.map((l, i) => (
                  <tr key={i}>
                    <td><b>{l.especie}</b></td>
                    <td className="mono">{mes(l.mes)}</td>
                    <td className="r">{n3(l.vendido_m3)}</td>
                    <td className="r">{n2(l.receita)}</td>
                    <td className="r">{n2(l.custo)}</td>
                    <td className="r" style={{
                      fontWeight: 700,
                      color: Number(l.margem) < 0 ? "var(--erro-texto)" : "var(--ok-num)",
                    }}>
                      {n2(l.margem)}
                    </td>
                    <td className="r" style={{
                      color: Number(l.sem_custo_m3) > 0 ? "var(--aten-num)" : "var(--texto-terciario)",
                    }}>
                      {n3(l.sem_custo_m3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="painel">
          <div className="tt">
            <span className="rotulo">Giro por bitola</span>
            <span className="cod" style={{ marginLeft: 10 }}>
              parado é capital em cima de serragem
            </span>
          </div>
          {giro === null ? <Esq /> : giro.length === 0 ? (
            <div className="vazio"><p>Sem estoque para analisar.</p></div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th>Produto</th><th>Bitola</th>
                  <th style={{ textAlign: "right" }}>Estoque m³</th>
                  <th style={{ textAlign: "right" }}>Vendido m³</th>
                  <th style={{ textAlign: "right" }}>Vendas</th>
                  <th style={{ textAlign: "right" }}>Parado</th>
                  <th style={{ textAlign: "right" }}>Giro</th>
                </tr>
              </thead>
              <tbody>
                {giro.map((l, i) => (
                  <tr key={i}>
                    <td className="mono">{l.codigo}</td>
                    <td className="mono">{bitola(l)}</td>
                    <td className="r" style={{
                      color: Number(l.em_estoque_m3) < 0 ? "var(--erro-texto)" : undefined,
                    }}>
                      {n3(l.em_estoque_m3)}
                    </td>
                    <td className="r">{n3(l.vendido_m3)}</td>
                    <td className="r">{l.vendas}</td>
                    <td className="r" style={{
                      color: (l.dias_parado ?? 0) > 60 ? "var(--aten-num)" : undefined,
                      fontWeight: (l.dias_parado ?? 0) > 60 ? 700 : undefined,
                    }}>
                      {l.dias_parado === null ? "—" : `${l.dias_parado}d`}
                    </td>
                    <td className="r">{l.giro ? Number(l.giro).toFixed(2) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function Esq() {
  return (
    <div className="esq">
      {[0, 1, 2].map((i) => (
        <div className="l" key={i}>
          <span className="b" /><span className="b" /><span className="b" /><span className="b" />
        </div>
      ))}
    </div>
  );
}
